import React, { useEffect, useRef, useState } from "react";
import { extractFromImage } from "./extractFromImage";
import {
  ApplyResult,
  MatchedRow,
  applyImports,
  matchAgainstRoster,
} from "./rosterMatching";
import { RosterPlayer, loadOwner, loadRoster } from "./roster";

interface Props {
  // Caller refreshes its roster snapshot after a successful apply so
  // the parent list reflects the new / updated players.
  onApplied: () => void;
  onClose: () => void;
}

// Stages the user moves through:
//   pick     → no file yet, file picker visible
//   preview  → file chosen, thumbnail + "Extract" button
//   verify   → extraction returned (possibly empty), checkbox table
//   done     → applied, summary + close
type Stage = "pick" | "preview" | "verify" | "done";

export default function RosterImport({ onApplied, onClose }: Props) {
  const [stage, setStage] = useState<Stage>("pick");
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [extractError, setExtractError] = useState<string | null>(null);
  const [rows, setRows] = useState<MatchedRow[]>([]);
  // Roster snapshot taken at extract time — powers the "link to existing
  // player" dropdown on unmatched rows.
  const [roster, setRoster] = useState<RosterPlayer[]>([]);
  const [result, setResult] = useState<ApplyResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Object URLs leak unless revoked when no longer needed. Cleanup runs
  // when the component unmounts or the URL is replaced.
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handlePick = (f: File) => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
    setExtractError(null);
    setStage("preview");
  };

  const handleExtract = async () => {
    if (!file) return;
    setExtracting(true);
    setExtractError(null);
    try {
      const extracts = await extractFromImage(file);
      const current = loadRoster();
      setRoster(current);
      setRows(matchAgainstRoster(extracts, current, loadOwner()));
      setStage("verify");
    } catch (err) {
      setExtractError((err as Error).message || "Could not read the image");
    } finally {
      setExtracting(false);
    }
  };

  const toggleRow = (idx: number) => {
    setRows((prev) =>
      prev.map((r, i) => (i === idx ? { ...r, selected: !r.selected } : r)),
    );
  };

  // Link an unmatched ("new") row to an existing roster player (or back to
  // "add new" when cleared). On apply this updates that player's handicap and
  // stores the detected name as an alias for future auto-matching.
  const setRowLink = (idx: number, linkToId: string | undefined) => {
    setRows((prev) =>
      prev.map((r, i) => (i === idx ? { ...r, linkToId } : r)),
    );
  };

  const allSelected = rows.length > 0 && rows.every((r) => r.selected);
  const toggleAll = () => {
    setRows((prev) => prev.map((r) => ({ ...r, selected: !allSelected })));
  };

  const handleApply = () => {
    const r = applyImports(rows);
    setResult(r);
    setStage("done");
    onApplied();
  };

  const reset = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(null);
    setPreviewUrl(null);
    setRows([]);
    setResult(null);
    setExtractError(null);
    setStage("pick");
  };

  return (
    <div className="cg-subpage cg-import-subpage">
      <div className="cg-subpage-head">
        <button className="cg-btn cg-btn-ghost" onClick={onClose}>
          ← Back
        </button>
        <h2>Import handicaps</h2>
      </div>

      {stage === "pick" && (
        <div className="cg-import-pick">
          <p className="cg-subpage-note">
            Upload a screenshot of your handicap list. Names that match
            your roster will have their handicap updated; new names can
            be added as new players.
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="cg-import-file"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handlePick(f);
            }}
          />
        </div>
      )}

      {stage === "preview" && previewUrl && (
        <div className="cg-import-preview">
          <img
            src={previewUrl}
            alt="Selected screenshot"
            className="cg-import-thumb"
          />
          {extractError && (
            <p className="cg-import-error">{extractError}</p>
          )}
          <div className="cg-import-actions">
            <button
              type="button"
              className="cg-btn cg-btn-ghost"
              onClick={reset}
              disabled={extracting}
            >
              Pick another
            </button>
            <button
              type="button"
              className="cg-btn cg-btn-primary"
              onClick={handleExtract}
              disabled={extracting}
            >
              {extracting ? "Extracting…" : "Extract handicaps"}
            </button>
          </div>
        </div>
      )}

      {stage === "verify" && (
        <div className="cg-import-verify">
          {rows.length === 0 ? (
            <>
              <p className="cg-subpage-note">
                No players detected in this image. Try a clearer
                screenshot, or pick another file.
              </p>
              <div className="cg-import-actions">
                <button
                  type="button"
                  className="cg-btn cg-btn-primary"
                  onClick={reset}
                >
                  Pick another
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="cg-subpage-note">
                Review what we found. Uncheck anything you don't want
                applied.
              </p>
              <table className="cg-import-table">
                <thead>
                  <tr>
                    <th className="cg-import-th-check">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleAll}
                        aria-label="Select all"
                      />
                    </th>
                    <th>Player</th>
                    <th>HI</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr
                      key={`${row.detectedName}-${i}`}
                      className={row.selected ? "" : "cg-import-row-skipped"}
                    >
                      <td>
                        <input
                          type="checkbox"
                          checked={row.selected}
                          onChange={() => toggleRow(i)}
                        />
                      </td>
                      <td className="cg-import-name">{row.detectedName}</td>
                      <td className="cg-import-hi">{row.handicapIndex}</td>
                      <td>
                        {row.matchType === "new" ? (
                          // Unmatched name — default to adding a new player, or
                          // let the user link it to someone they've renamed.
                          <select
                            className="cg-import-link"
                            value={row.linkToId || ""}
                            onChange={(e) =>
                              setRowLink(i, e.target.value || undefined)
                            }
                          >
                            <option value="">Add new player</option>
                            {roster.map((p) => (
                              <option key={p.id} value={p.id}>
                                Update {p.name}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span
                            className={`cg-import-badge cg-import-badge-${row.matchType}`}
                          >
                            {row.matchType === "update-owner"
                              ? "Update yours"
                              : "Update existing"}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="cg-import-actions">
                <button
                  type="button"
                  className="cg-btn cg-btn-ghost"
                  onClick={reset}
                >
                  Start over
                </button>
                <button
                  type="button"
                  className="cg-btn cg-btn-primary"
                  onClick={handleApply}
                  disabled={!rows.some((r) => r.selected)}
                >
                  Apply Updates
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {stage === "done" && result && (
        <div className="cg-import-done">
          <p className="cg-import-summary">
            <strong>{result.updated}</strong> updated ·{" "}
            <strong>{result.added}</strong> added
            {result.ownerUpdated && <> · your handicap updated</>}
            {result.skipped > 0 && <> · {result.skipped} skipped</>}
          </p>
          <div className="cg-import-actions">
            <button
              type="button"
              className="cg-btn cg-btn-ghost"
              onClick={reset}
            >
              Import another
            </button>
            <button
              type="button"
              className="cg-btn cg-btn-primary"
              onClick={onClose}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
